/**
 * Date math for the Calendar tool — deliberately no date library (the repo has
 * none; storage stays ISO-8601 UTC via toISOString(), display is device-local
 * through the native Date, matching boardsStore/TaskDetailModal conventions).
 *
 * Everything here works on LOCAL calendar days: a "day" is what the user's
 * wall clock calls a day, so grids stay correct across DST transitions —
 * addDays goes through setDate (calendar arithmetic), never `+ 86400000 ms`
 * (a DST Sunday is 23 or 25 hours long and ms math drifts the grid by an
 * hour — see calendarTimeGridMath.test.js's Europe/Vienna weekend).
 */
import i18n from '../i18n';
import { appDateLocale, PARSEABLE_TIME_LOCALE } from './appLocale';

export const MINUTES_PER_DAY = 24 * 60;

/** Local midnight of the given date (new Date instance). */
export function startOfDay(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

/** Calendar-day addition (DST-safe: 23/25h days still land on midnight). */
export function addDays(date, days) {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

export function addMonths(date, months) {
  const d = new Date(date);
  const day = d.getDate();
  d.setDate(1);
  d.setMonth(d.getMonth() + months);
  // Clamp (Jan 31 + 1 month → Feb 28/29, not Mar 3).
  d.setDate(Math.min(day, daysInMonth(d.getFullYear(), d.getMonth())));
  return d;
}

export function daysInMonth(year, monthIndex) {
  return new Date(year, monthIndex + 1, 0).getDate();
}

export function isSameDay(a, b) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

export function isSameMonth(a, b) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth();
}

/** Monday-based start of week (Outlook/Google default in EU locales). */
export function startOfWeek(date, weekStartsOn = 1) {
  const d = startOfDay(date);
  const diff = (d.getDay() - weekStartsOn + 7) % 7;
  return addDays(d, -diff);
}

/** The 7 local days of `date`'s week. */
export function weekDays(date, weekStartsOn = 1) {
  const start = startOfWeek(date, weekStartsOn);
  return Array.from({ length: 7 }, (_, i) => addDays(start, i));
}

/**
 * The fixed 6×7 = 42 local days a month view shows (leading/trailing days of
 * the surrounding months included). Fixed height on purpose: the grid never
 * jumps between 4/5/6 rows while paging months (Google Calendar behaviour).
 */
export function monthGridDays(date, weekStartsOn = 1) {
  const firstOfMonth = new Date(date.getFullYear(), date.getMonth(), 1);
  const gridStart = startOfWeek(firstOfMonth, weekStartsOn);
  return Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));
}

/** Minutes since local midnight for an instant (grid row math). */
export function minutesOfDay(date) {
  return date.getHours() * 60 + date.getMinutes();
}

/** Bare local YYYY-MM-DD (all-day storage form; task dueDate precedent). */
export function toDateKey(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** Parse YYYY-MM-DD as LOCAL midnight (never `new Date(str)` — that's UTC). */
export function fromDateKey(key) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(key || ''));
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

/**
 * The [start, end) instants an event occupies, as local Dates. All-day events
 * expand their bare date keys to local midnights (end_date is inclusive in
 * the record — Outlook editing convention — so the exclusive end is +1 day).
 */
export function eventRange(event) {
  if (event.all_day) {
    const start = fromDateKey(event.start_date) || startOfDay(new Date());
    const endKey = fromDateKey(event.end_date) || start;
    return { start, end: addDays(startOfDay(endKey), 1) };
  }
  return { start: new Date(event.start), end: new Date(event.end) };
}

/** True when the event intersects the local day (end exclusive). */
export function eventTouchesDay(event, day) {
  const dayStart = startOfDay(day);
  const dayEnd = addDays(dayStart, 1);
  const { start, end } = eventRange(event);
  return start < dayEnd && end > dayStart;
}

/** Events intersecting [rangeStart, rangeEnd), sorted by start. */
export function eventsInRange(events, rangeStart, rangeEnd) {
  return (events || [])
    .filter((e) => {
      const { start, end } = eventRange(e);
      return start < rangeEnd && end > rangeStart;
    })
    .sort((a, b) => eventRange(a).start - eventRange(b).start);
}

/**
 * Pixel/slot geometry for one event on one TimeGrid day column: the top and
 * height in MINUTES clipped to that day (the caller multiplies by px/min).
 */
export function eventDaySegment(event, day) {
  const dayStart = startOfDay(day);
  const dayEnd = addDays(dayStart, 1);
  const { start, end } = eventRange(event);
  if (start >= dayEnd || end <= dayStart) return null;
  const from = start < dayStart ? 0 : minutesOfDay(start);
  const to = end > dayEnd ? MINUTES_PER_DAY : Math.max(minutesOfDay(end), from + 1);
  return { topMinutes: from, durationMinutes: Math.max(to - from, 15) };
}

/**
 * Assign overlapping timed events of one day to side-by-side columns
 * (Outlook/Google overlap layout). Returns [{ event, column, columns }].
 * Greedy interval-partitioning per overlap cluster.
 */
export function layoutDayEvents(events, day) {
  const segs = [];
  for (const event of events) {
    if (event.all_day) continue;
    const seg = eventDaySegment(event, day);
    if (seg) segs.push({ event, ...seg });
  }
  segs.sort((a, b) => a.topMinutes - b.topMinutes || b.durationMinutes - a.durationMinutes);

  const placed = [];
  let cluster = [];
  let clusterEnd = -1;
  const flush = () => {
    const columns = Math.max(1, ...cluster.map((s) => s.column + 1));
    for (const s of cluster) placed.push({ ...s, columns });
    cluster = [];
  };
  for (const seg of segs) {
    if (cluster.length > 0 && seg.topMinutes >= clusterEnd) flush();
    const ends = [];
    for (const s of cluster) ends[s.column] = Math.max(ends[s.column] || 0, s.topMinutes + s.durationMinutes);
    let column = 0;
    while ((ends[column] || 0) > seg.topMinutes) column += 1;
    cluster.push({ ...seg, column });
    clusterEnd = Math.max(clusterEnd, seg.topMinutes + seg.durationMinutes);
  }
  if (cluster.length > 0) flush();
  return placed;
}

/** Snap minutes to the nearest slot (default 30-minute grid). */
export function snapMinutes(minutes, step = 30) {
  const snapped = Math.round(minutes / step) * step;
  return Math.min(Math.max(snapped, 0), MINUTES_PER_DAY);
}

/** Floor-snap (drag-create anchors to the slot the pointer went down in). */
export function floorMinutes(minutes, step = 30) {
  const snapped = Math.floor(minutes / step) * step;
  return Math.min(Math.max(snapped, 0), MINUTES_PER_DAY - step);
}

/** A local Date at `day` + `minutes` past local midnight. */
export function dayAtMinutes(day, minutes) {
  const d = startOfDay(day);
  d.setMinutes(minutes);
  return d;
}

/* ── display formatting ─────────────────────────────────────────────────────
 * Locale rule (30.08.2026): every DATE label follows the APP LANGUAGE via
 * appDateLocale(), never `undefined` (which is the OS locale — a German UI on
 * an English Mac read "August 2026"). The one deliberate exception is
 * formatTime: its output is re-typed and re-parsed by TimeCombobox, whose
 * parser only understands `a|am|p|pm`, so it is PINNED to en-US.
 * ------------------------------------------------------------------------- */

/**
 * Clock label in the user's 12h/24h preference.
 *
 * PINNED to en-US on purpose — this string round-trips through
 * `TimeCombobox.parseTimeInput`, which accepts only `a|am|p|pm`. ICU may
 * render de-DE `hour12` as `vorm./nachm.`; Node's ICU 77 happens not to, but
 * the packaged WKWebView/WebView2 carries a different ICU. Passing `undefined`
 * (the old behaviour) already carried this bug on a German machine.
 * Guarded by calendarLocaleFormatting.test.js's round-trip.
 */
export function formatTime(date, timeFormat) {
  return date.toLocaleTimeString(PARSEABLE_TIME_LOCALE, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: timeFormat === '12h',
  });
}

export function formatDayLong(date) {
  return date.toLocaleDateString(appDateLocale(), {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

export function formatMonthYear(date) {
  return date.toLocaleDateString(appDateLocale(), { month: 'long', year: 'numeric' });
}

/** Toolbar title for the visible range of a view. */
export function formatRangeTitle(view, date, weekStartsOn = 1) {
  if (view === 'day') return formatDayLong(date);
  if (view === 'week') {
    const days = weekDays(date, weekStartsOn);
    const first = days[0];
    const last = days[6];
    const sameMonth = isSameMonth(first, last);
    const firstStr = first.toLocaleDateString(appDateLocale(), {
      month: 'short',
      day: 'numeric',
    });
    const lastStr = last.toLocaleDateString(appDateLocale(), {
      month: sameMonth ? undefined : 'short',
      day: 'numeric',
      year: 'numeric',
    });
    return `${firstStr} – ${lastStr}`;
  }
  if (view === 'agenda') return i18n.t('calendar.agenda.upcoming');
  return formatMonthYear(date);
}

/** "09:00 – 10:30" / "All day" summary for chips/popovers. */
export function formatEventTime(event, timeFormat) {
  if (event.all_day) return i18n.t('calendar.allDay');
  const { start, end } = eventRange(event);
  return `${formatTime(start, timeFormat)} – ${formatTime(end, timeFormat)}`;
}

/**
 * The 7 weekday column headers for a grid starting on `weekStartsOn`, in the
 * app language. Derived from Intl rather than a hardcoded ['Mon', …] array:
 * fewer keys than a translation table and automatically right (de-DE short
 * weekdays are Mo, Di, Mi, Do, Fr, Sa, So).
 *
 * `style`: 'short' (Mon / Mo) or 'narrow' (M / M) — the RecurrenceEditor's
 * BYDAY chips use narrow. The RRULE codes themselves stay MO/TU/… on the wire.
 */
export function weekdayLabels(style = 'short', weekStartsOn = 1) {
  // 2026-01-04 is a Sunday, so +weekStartsOn lands on the requested first day.
  const anchor = new Date(2026, 0, 4);
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(anchor);
    d.setDate(anchor.getDate() + weekStartsOn + i);
    return d.toLocaleDateString(appDateLocale(), { weekday: style });
  });
}

/** Narrow weekday label for one RRULE BYDAY code ('MO' → 'M' / 'M'). */
const BYDAY_ORDER = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'];
export function weekdayLabelForByDay(code, style = 'narrow') {
  const idx = BYDAY_ORDER.indexOf(String(code).toUpperCase());
  if (idx < 0) return String(code);
  const anchor = new Date(2026, 0, 4); // Sunday
  const d = new Date(anchor);
  d.setDate(anchor.getDate() + idx);
  return d.toLocaleDateString(appDateLocale(), { weekday: style });
}
